import React, { useCallback, useEffect, useState, useRef, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Cell,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/layout/Navbar';
import { FilterBar } from '@/components/filters/FilterBar';
import { DotRating } from '@/components/ui/DotRating';
import { StatusDot } from '@/components/ui/Badge';
import { SavedToast } from '@/components/ui/SavedToast';
import { ICON_MAP } from '@/components/modals/IconPicker';
import { VoteButton } from '@/components/scoring/VoteButton';
import { ClassificationFooter } from '@/components/ui/ClassificationFooter';
import { INITIATIVE_TITLE_LINK_CLASS } from '@/utils/uiConventions';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  scoringApi,
  type ScoreLogRow,
} from '@/services/api';
import {
  ListBulletIcon,
  ChartBarSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

// Per-initiative score data merged onto a filtered store initiative
interface ScoreData {
  impact: number | null;
  effort: number | null;
  voteCount: number;
  hasVoted: boolean;
}

type SortKey = 'votes' | 'impact' | 'effort';

// Build the id -> score-data map from the scoring API rows (module scope so the
// reference is stable and usable inside memoized callbacks).
function buildScoreMap(rows: ScoreLogRow[]): Record<string, ScoreData> {
  const map: Record<string, ScoreData> = {};
  for (const row of rows) {
    map[row.id] = {
      impact: row.impact,
      effort: row.effort,
      voteCount: row.voteCount,
      hasVoted: row.hasVoted,
    };
  }
  return map;
}

interface ScoringLogPageProps {
  mode?: 'list' | 'chart';
}

// --- TruncatedTitle (local copy per CLAUDE.md pattern) ---

function TruncatedTitle({
  title,
  className,
  onClick,
}: {
  title: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    const el = titleRef.current;
    if (el && el.scrollWidth > el.clientWidth) setShowTooltip(true);
  };

  return (
    <div className="relative min-w-0 flex-1">
      <span
        ref={titleRef}
        className={`truncate block ${className || ''}`}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {title}
      </span>
      {showTooltip && (
        <div className="absolute top-full left-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded z-50 max-w-md break-words whitespace-normal">
          {title}
        </div>
      )}
    </div>
  );
}

export function ScoringLogPage({ mode = 'list' }: ScoringLogPageProps) {
  const navigate = useNavigate();
  const {
    products,
    statuses,
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setLoading,
    setError,
    refreshTrigger,
    openViewInitiativeModal,
    initiativeModalOpen,
    flashSaved,
  } = useAppStore();
  const fc = useFieldConfig();
  const { user } = useAuth();

  const [scoreMap, setScoreMap] = useState<Record<string, ScoreData>>({});
  const [filteredInitiatives, setFilteredInitiatives] = useState<any[]>([]);
  const [onlyScored, setOnlyScored] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('votes');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [dataLoading, setDataLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const prevModalOpenRef = useRef(false);
  const viewContainerRef = useRef<HTMLDivElement>(null);
  // Latest merged rows, kept in a ref so the (stable) open-record handler can look
  // up the full row by id without depending on workingRows (which changes on edits).
  const workingRowsRef = useRef<any[]>([]);

  const toggleSort = useCallback((key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }, [sortKey]);

  const canEditRow = useCallback((ownerId?: string | null): boolean => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    if (user.role === 'EDITOR') return !ownerId || ownerId === user.id;
    return false;
  }, [user]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setDataLoading(true);
      setDataError(null);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        scoreRows,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        scoringApi.getAll(),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter((sp: any) => sp.productId === product.id),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
      setQuarters(allQuarters);

      setScoreMap(buildScoreMap(scoreRows));
    } catch (err) {
      console.error('Failed to load scoring data:', err);
      setError('Failed to load scoring data');
      setDataError('Failed to load scoring data');
    } finally {
      setLoading(false);
      setDataLoading(false);
      setHasLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  // When the initiative modal closes, silently re-fetch scores so the grid/chart
  // stays fresh after edits on the Scoring tab (which auto-save without a full refresh)
  useEffect(() => {
    if (prevModalOpenRef.current && !initiativeModalOpen) {
      scoringApi.getAll()
        .then((rows) => setScoreMap(buildScoreMap(rows)))
        .catch(console.error);
    }
    prevModalOpenRef.current = initiativeModalOpen;
  }, [initiativeModalOpen]);

  // Working rows: FilterBar output → merge score data → "only scored" filter → sort
  const workingRows = useMemo(() => {
    const rows = filteredInitiatives
      .map((init) => {
        const s = scoreMap[init.id] ?? { impact: null, effort: null, voteCount: 0, hasVoted: false };
        return { ...init, ...s };
      })
      .filter((row) => {
        if (!onlyScored) return true;
        return row.impact != null || row.effort != null || row.voteCount > 0;
      });

    const desc = (a: number | null, b: number | null) => {
      const av = a == null ? -Infinity : a;
      const bv = b == null ? -Infinity : b;
      return bv - av;
    };

    const sorted = [...rows].sort((a, b) => {
      if (sortKey === 'votes') return (b.voteCount - a.voteCount) || desc(a.impact, b.impact);
      if (sortKey === 'impact') return desc(a.impact, b.impact) || (b.voteCount - a.voteCount);
      return desc(a.effort, b.effort) || (b.voteCount - a.voteCount);
    });

    return sortDir === 'asc' ? sorted.reverse() : sorted;
  }, [filteredInitiatives, scoreMap, onlyScored, sortKey, sortDir]);

  workingRowsRef.current = workingRows;

  // Chart points — only items with BOTH impact and effort set
  const chartPoints = useMemo(() => {
    return workingRows
      .filter((r) => r.impact != null && r.effort != null)
      .map((r) => ({
        id: r.id,
        title: r.title,
        impact: r.impact as number,
        effort: r.effort as number,
        votes: r.voteCount,
        impactScore: (r.impact as number) * (r.voteCount + 1),
        color: r.classification?.color || '#3B82F6',
        raw: r,
      }));
  }, [workingRows]);

  const unscoredHidden = workingRows.length - chartPoints.length;

  // --- Inline edit handlers ---

  const handleScoreChange = useCallback(
    (id: string, next: { impact: number | null; effort: number | null }) => {
      setScoreMap((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] ?? { voteCount: 0, hasVoted: false, impact: null, effort: null }),
          impact: next.impact,
          effort: next.effort,
        },
      }));
      scoringApi.update(id, next).then(flashSaved).catch((err) => {
        console.error('Failed to save score:', err);
        scoringApi.getAll().then((rows) => setScoreMap(buildScoreMap(rows))).catch(() => {});
      });
    },
    [flashSaved]
  );

  const handleStatusChange = useCallback(
    (id: string, statusId: string) => {
      const newStatus = statuses.find((s) => s.id === statusId);
      // Optimistically update the store so the select + status objects stay in sync
      const current = useAppStore.getState().initiatives;
      setInitiatives(
        current.map((init) =>
          init.id === id ? { ...init, statusId, status: newStatus ?? init.status } : init
        )
      );
      initiativeApi.update(id, { statusId }).then(flashSaved).catch((err) => {
        console.error('Failed to update status:', err);
        loadData();
      });
    },
    [statuses, flashSaved, loadData, setInitiatives]
  );

  const handleVoteChange = useCallback(
    (id: string, count: number, hasVoted: boolean) => {
      setScoreMap((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] ?? { impact: null, effort: null }),
          voteCount: count,
          hasVoted,
        } as ScoreData,
      }));
      flashSaved();
    },
    [flashSaved]
  );

  // Resolve the full (merged) row by id from a ref so this stays referentially
  // stable — opening a record must NOT recreate this callback (which would force
  // the whole table to re-render before the modal appears).
  const handleOpen = useCallback(
    (id: string) => {
      const row = workingRowsRef.current.find((r) => r.id === id);
      if (row) openViewInitiativeModal(row);
    },
    [openViewInitiativeModal]
  );

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden relative">
        <SavedToast className="absolute top-2 right-6 z-40" />
        <div className="h-full flex flex-col">
          {/* FilterBar */}
          <div className="px-3 sm:px-6 py-3 bg-background-primary">
            <FilterBar
              onFilteredInitiativesChange={setFilteredInitiatives}
              viewType="list"
              savedViewKey="scoring"
              viewContainerRef={viewContainerRef}
              customCount={mode === 'chart' ? chartPoints.length : workingRows.length}
              customCountLabel={mode === 'chart' ? 'Plotted' : 'Rows'}
              hideHasDependencies
              hideExport
              scoreMap={scoreMap}
              showScoreFilters
            />
          </div>

          {/* Page header + controls — kept OUT of the scroll container so the
              title, "Only scored" toggle and List/Chart switch stay visible on scroll */}
          <div className="px-6 pt-1 pb-4 bg-background-primary">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-xl font-semibold text-text-primary">Scoring</h1>
                <p className="text-sm text-text-secondary mt-0.5">
                  Prioritise {fc.label('initiative').toLowerCase()}s by Impact, Effort and votes.
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Only-scored toggle (list mode only) */}
                {mode === 'list' && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <button
                      role="switch"
                      aria-checked={onlyScored}
                      onClick={() => setOnlyScored(!onlyScored)}
                      className={`relative flex-shrink-0 w-8 h-4 rounded-full transition-colors ${
                        onlyScored ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${
                          onlyScored ? 'translate-x-4' : ''
                        }`}
                      />
                    </button>
                    <span className="text-xs text-text-secondary whitespace-nowrap">Only scored</span>
                  </label>
                )}

                {/* View switch: List | Chart */}
                <div className="flex items-center gap-1 bg-surface-subtle rounded-md p-0.5">
                  <button
                    onClick={() => navigate('/scoring')}
                    title="List view"
                    className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                      mode === 'list'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <ListBulletIcon className="w-4 h-4" />
                    List
                  </button>
                  <button
                    onClick={() => navigate('/scoring/chart')}
                    title="Impact vs Effort chart"
                    className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                      mode === 'chart'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <ChartBarSquareIcon className="w-4 h-4" />
                    Chart
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-auto px-6 pb-6" ref={viewContainerRef}>
            {dataError ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                <p className="text-sm text-text-secondary">
                  Couldn't load scoring data. Check your connection and try again.
                </p>
                <button
                  onClick={() => loadData()}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : dataLoading && !hasLoaded ? null : mode === 'list' ? (
              <ScoringTable
                rows={workingRows}
                products={products}
                statuses={statuses}
                canEditRow={canEditRow}
                sortKey={sortKey}
                sortDir={sortDir}
                onToggleSort={toggleSort}
                onScoreChange={handleScoreChange}
                onStatusChange={handleStatusChange}
                onVoteChange={handleVoteChange}
                onOpen={handleOpen}
              />
            ) : (
              <ImpactEffortChart
                points={chartPoints}
                unscoredHidden={unscoredHidden}
                onOpen={(row) => openViewInitiativeModal(row)}
              />
            )}
          </div>

          <ClassificationFooter />
        </div>
      </main>
    </div>
  );
}

// --- Editable status cell — matches List view (dot + name) but stays editable ---

function StatusCell({
  id,
  statusId,
  statusName,
  statusColor,
  statuses,
  editable,
  onChange,
}: {
  id: string;
  statusId: string;
  statusName: string;
  statusColor: string;
  statuses: any[];
  editable: boolean;
  onChange: (id: string, statusId: string) => void;
}) {
  if (!editable) {
    return (
      <div className="flex items-center gap-2">
        <StatusDot color={statusColor || '#9CA3AF'} />
        <span className="text-sm text-text-secondary">{statusName || '—'}</span>
      </div>
    );
  }

  return (
    <div className="relative inline-flex items-center gap-2 -mx-1.5 px-1.5 py-1 rounded hover:bg-surface-hover cursor-pointer">
      <StatusDot color={statusColor || '#9CA3AF'} />
      <span className="text-sm text-text-secondary">{statusName || '—'}</span>
      <ChevronDownIcon className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
      <select
        value={statusId}
        onChange={(e) => onChange(id, e.target.value)}
        aria-label="Change status"
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      >
        {statuses.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
    </div>
  );
}

// --- Sortable column header (Impact / Effort / Votes) ---

function SortHeader({
  label,
  columnKey,
  sortKey,
  sortDir,
  onToggleSort,
}: {
  label: string;
  columnKey: SortKey;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onToggleSort: (key: SortKey) => void;
}) {
  const active = sortKey === columnKey;
  return (
    <th className="text-left py-2 px-4 font-semibold text-text-primary text-sm whitespace-nowrap">
      <button
        type="button"
        onClick={() => onToggleSort(columnKey)}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`inline-flex items-center gap-1 transition-colors ${
          active ? 'text-blue-600' : 'text-text-primary hover:text-blue-600'
        }`}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUpIcon className="w-3.5 h-3.5 flex-shrink-0" />
          ) : (
            <ChevronDownIcon className="w-3.5 h-3.5 flex-shrink-0" />
          )
        ) : (
          <ChevronUpDownIcon className="w-3.5 h-3.5 flex-shrink-0 text-text-tertiary" />
        )}
      </button>
    </th>
  );
}

// --- Single scoring row (memoized) ---
// Receives PRIMITIVE props only so React.memo can skip re-rendering every row when
// an unrelated row's score changes — only the edited row re-renders.
interface ScoringRowProps {
  id: string;
  title: string;
  classificationColor: string;
  isStarred: boolean;
  statusId: string;
  statusName: string;
  statusColor: string;
  statuses: any[];
  impact: number | null;
  effort: number | null;
  voteCount: number;
  hasVoted: boolean;
  editable: boolean;
  onOpen: (id: string) => void;
  onScoreChange: (id: string, next: { impact: number | null; effort: number | null }) => void;
  onStatusChange: (id: string, statusId: string) => void;
  onVoteChange: (id: string, count: number, hasVoted: boolean) => void;
}

const ScoringRow = React.memo(function ScoringRow({
  id,
  title,
  classificationColor,
  isStarred,
  statusId,
  statusName,
  statusColor,
  statuses,
  impact,
  effort,
  voteCount,
  hasVoted,
  editable,
  onOpen,
  onScoreChange,
  onStatusChange,
  onVoteChange,
}: ScoringRowProps) {
  return (
    <tr className="border-b border-gray-100 hover:bg-surface-hover">
      {/* Title */}
      <td className="py-2 px-4 pl-12 max-w-md">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-1 h-8 rounded-full flex-shrink-0"
            style={{ backgroundColor: classificationColor || '#3B82F6' }}
          />
          {isStarred && <StarSolid className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
          <TruncatedTitle
            title={title}
            className={INITIATIVE_TITLE_LINK_CLASS}
            onClick={() => onOpen(id)}
          />
        </div>
      </td>

      {/* Status — dot + name, editable for owners/admins */}
      <td className="py-2 px-4 whitespace-nowrap">
        <StatusCell
          id={id}
          statusId={statusId}
          statusName={statusName}
          statusColor={statusColor}
          statuses={statuses}
          editable={editable}
          onChange={onStatusChange}
        />
      </td>

      {/* Impact */}
      <td className="py-2 px-4 whitespace-nowrap">
        <DotRating
          value={impact ?? 0}
          color="green"
          editable={editable}
          size="sm"
          onChange={(v) => onScoreChange(id, { impact: v || null, effort: effort ?? null })}
        />
      </td>

      {/* Effort */}
      <td className="py-2 px-4 whitespace-nowrap">
        <DotRating
          value={effort ?? 0}
          color="purple"
          editable={editable}
          size="sm"
          onChange={(v) => onScoreChange(id, { impact: impact ?? null, effort: v || null })}
        />
      </td>

      {/* Votes */}
      <td className="py-2 px-4 whitespace-nowrap">
        <VoteButton
          initiativeId={id}
          count={voteCount}
          hasVoted={hasVoted}
          size="sm"
          onChange={(c, v) => onVoteChange(id, c, v)}
        />
      </td>
    </tr>
  );
});

// --- List table — grouped by Product > Sub-product (mirrors the main List view) ---

const ScoringTable = React.memo(function ScoringTable({
  rows,
  products,
  statuses,
  canEditRow,
  sortKey,
  sortDir,
  onToggleSort,
  onScoreChange,
  onStatusChange,
  onVoteChange,
  onOpen,
}: {
  rows: any[];
  products: any[];
  statuses: any[];
  canEditRow: (ownerId?: string | null) => boolean;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onToggleSort: (key: SortKey) => void;
  onScoreChange: (id: string, next: { impact: number | null; effort: number | null }) => void;
  onStatusChange: (id: string, statusId: string) => void;
  onVoteChange: (id: string, count: number, hasVoted: boolean) => void;
  onOpen: (id: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-text-secondary text-sm">
        No initiatives to show. Try adjusting filters or turning off "Only scored".
      </div>
    );
  }

  // Group rows by sub-product, preserving the incoming sort order within each group
  const rowsBySubProduct = new Map<string, any[]>();
  for (const row of rows) {
    const key = row.subProductId;
    if (!rowsBySubProduct.has(key)) rowsBySubProduct.set(key, []);
    rowsBySubProduct.get(key)!.push(row);
  }

  const renderRow = (row: any) => (
    <ScoringRow
      key={row.id}
      id={row.id}
      title={row.title}
      classificationColor={row.classification?.color || '#3B82F6'}
      isStarred={!!row.isStarred}
      statusId={row.statusId}
      statusName={row.status?.name || ''}
      statusColor={row.status?.color || ''}
      statuses={statuses}
      impact={row.impact ?? null}
      effort={row.effort ?? null}
      voteCount={row.voteCount}
      hasVoted={row.hasVoted}
      editable={canEditRow(row.userId)}
      onOpen={onOpen}
      onScoreChange={onScoreChange}
      onStatusChange={onStatusChange}
      onVoteChange={onVoteChange}
    />
  );

  return (
    <table className="w-full border-collapse">
      <thead className="sticky top-0 z-10 bg-background-secondary">
        <tr className="border-b border-gray-200">
          <th className="text-left py-2 px-4 font-semibold text-text-primary text-sm">Initiative</th>
          <th className="text-left py-2 px-4 font-semibold text-text-primary text-sm whitespace-nowrap">Status</th>
          <SortHeader label="Impact" columnKey="impact" sortKey={sortKey} sortDir={sortDir} onToggleSort={onToggleSort} />
          <SortHeader label="Effort" columnKey="effort" sortKey={sortKey} sortDir={sortDir} onToggleSort={onToggleSort} />
          <SortHeader label="Votes" columnKey="votes" sortKey={sortKey} sortDir={sortDir} onToggleSort={onToggleSort} />
        </tr>
      </thead>
      <tbody>
        {products.map((product) => {
          const subProducts = product.subProducts || [];
          const productCount = subProducts.reduce(
            (count: number, sp: any) => count + (rowsBySubProduct.get(sp.id)?.length || 0),
            0
          );

          // Hide products with no matching rows
          if (productCount === 0) return null;

          return (
            <React.Fragment key={product.id}>
              {/* Product header row */}
              <tr className="bg-blue-50/50 border-b border-gray-100">
                <td colSpan={5} className="py-2 px-4 font-semibold text-text-primary text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    {product.icon && ICON_MAP[product.icon] && (
                      <span style={{ color: product.iconColor || '#6B7280' }}>
                        {(() => {
                          const IconComponent = ICON_MAP[product.icon];
                          return <IconComponent className="w-4 h-4 flex-shrink-0" />;
                        })()}
                      </span>
                    )}
                    <span className="truncate" title={product.name}>{product.name}</span>
                    <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                      {productCount}
                    </span>
                  </div>
                </td>
              </tr>

              {/* Sub-product groups */}
              {subProducts.map((subProduct: any) => {
                const spRows = rowsBySubProduct.get(subProduct.id) || [];
                if (spRows.length === 0) return null;

                return (
                  <React.Fragment key={subProduct.id}>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <td colSpan={5} className="py-1.5 px-3 pl-8 font-medium text-sm text-text-secondary">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate" title={subProduct.name}>{subProduct.name}</span>
                          <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                            {spRows.length}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {spRows.map(renderRow)}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
});

// --- Impact vs Effort bubble chart ---

function ImpactEffortChart({
  points,
  unscoredHidden,
  onOpen,
}: {
  points: any[];
  unscoredHidden: number;
  onOpen: (row: any) => void;
}) {
  // Defer the (potentially thousands of) plotted points so that applying a filter
  // updates the rest of the page instantly while React re-renders the heavy chart
  // in a non-blocking pass. `isStale` lets us softly dim the chart while it catches
  // up, so the transition reads as intentional rather than janky.
  const deferredPoints = useDeferredValue(points);
  const isStale = deferredPoints !== points;

  // Bubbles share only 25 discrete (Impact, Effort) cells, so many stack exactly
  // on top of each other. Spread the bubbles within their cell deterministically
  // (phyllotaxis / golden-angle layout) so each is visible and clickable, while
  // keeping the real Impact/Effort for the tooltip and bubble size.
  const jittered = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const p of deferredPoints) {
      const key = `${p.impact}-${p.effort}`;
      (groups[key] ||= []).push(p);
    }
    const maxR = 0.34; // stays < 0.5 so points never leave their cell
    const goldenAngle = 2.399963229728653;
    const out: any[] = [];
    for (const key of Object.keys(groups)) {
      const g = groups[key]
        .slice()
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)); // stable across renders
      const n = g.length;
      g.forEach((pt, i) => {
        let dx = 0;
        let dy = 0;
        if (n > 1) {
          const r = maxR * Math.sqrt((i + 0.5) / n);
          const theta = i * goldenAngle;
          dx = r * Math.cos(theta);
          dy = r * Math.sin(theta);
        }
        out.push({ ...pt, plotEffort: pt.effort + dx, plotImpact: pt.impact + dy });
      });
    }
    return out;
  }, [deferredPoints]);

  if (deferredPoints.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-text-secondary text-sm gap-1">
        <p>No initiatives have both an Impact and Effort score yet.</p>
        <p className="text-xs text-text-tertiary">Set both ratings on an initiative's Scoring tab to plot it here.</p>
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div className="bg-gray-900 text-white rounded px-3 py-2 text-xs max-w-xs">
        <p className="font-semibold mb-1 break-words">{p.title}</p>
        <p>Impact: {p.impact} / 5</p>
        <p>Effort: {p.effort} / 5</p>
        <p>Votes: {p.votes}</p>
        <p className="text-gray-300 mt-1">Impact Score: {p.impactScore}</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="relative bg-white border border-gray-200 rounded-lg p-4 h-[calc(100vh-280px)] min-h-[480px]">
        {/* Explicit feedback while the (deferred) chart re-renders thousands of
            bubbles — without this the 3–5s draw reads as a frozen/blank chart. */}
        {isStale && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 text-xs text-text-secondary bg-white/90 rounded-full px-3 py-1 shadow-sm">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-200 border-t-blue-500 animate-spin" />
            <span>Rendering chart…</span>
          </div>
        )}
        <div
          className="h-full"
          style={{ opacity: isStale ? 0.6 : 1, transition: 'opacity 0.2s ease-out' }}
        >
          <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
            {/* Cell boundaries at the half-integers so each (Effort, Impact)
                square is visible (e.g. Effort 3 × Impact 5). */}
            {[0.5, 1.5, 2.5, 3.5, 4.5, 5.5].map((v) => (
              <ReferenceLine key={`gx-${v}`} x={v} stroke="#F1F3F5" strokeWidth={1} />
            ))}
            {[0.5, 1.5, 2.5, 3.5, 4.5, 5.5].map((v) => (
              <ReferenceLine key={`gy-${v}`} y={v} stroke="#F1F3F5" strokeWidth={1} />
            ))}
            <ReferenceLine x={3} stroke="#E5E7EB" strokeDasharray="4 4" />
            <ReferenceLine y={3} stroke="#E5E7EB" strokeDasharray="4 4" />
            <XAxis
              type="number"
              dataKey="plotEffort"
              name="Effort"
              domain={[0.5, 5.5]}
              ticks={[1, 2, 3, 4, 5]}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={false}
              label={{ value: 'Effort →', position: 'bottom', offset: 10, fontSize: 12, fill: '#374151' }}
            />
            <YAxis
              type="number"
              dataKey="plotImpact"
              name="Impact"
              domain={[0.5, 5.5]}
              ticks={[1, 2, 3, 4, 5]}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={false}
              label={{ value: 'Impact →', angle: -90, position: 'left', offset: 0, fontSize: 12, fill: '#374151' }}
            />
            <ZAxis type="number" dataKey="impactScore" range={[80, 900]} name="Impact Score" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={renderTooltip} />
            <Scatter
              data={jittered}
              onClick={(d: any) => d && d.raw && onOpen(d.raw)}
              cursor="pointer"
              isAnimationActive={jittered.length <= 400}
            >
              {jittered.map((p) => (
                <Cell key={p.id} fill={p.color} fillOpacity={0.85} stroke={p.color} strokeOpacity={1} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-text-tertiary">
        <p>Bubble size = Impact Score (Impact × (Votes + 1)). Colour = classification. Bubbles sharing a score are spread out — click any bubble to open it.</p>
        {unscoredHidden > 0 && (
          <p>{unscoredHidden} {unscoredHidden === 1 ? 'initiative' : 'initiatives'} hidden (missing Impact or Effort).</p>
        )}
      </div>
    </div>
  );
}
